import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Briefcase, Loader2, RotateCcw, Send, Building2,
    CreditCard, Landmark, ShieldCheck, FileStack, ClipboardList,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { inputCls, Label, SelectIcon, SectionHeader, InnerHeader, ToggleYesNo } from '../../components/vendor/sharedVendorUI';
import GroupCascadeFields from '../../components/vendor/GroupCascadeFields';
import GSTDetailsTable, { newGSTRow, validateGSTRows } from '../../components/vendor/GSTDetailsTable';
import MSMEFields from '../../components/vendor/MSMEFields';
import DocumentUploadField from '../../components/vendor/DocumentUploadField';
import { uploadFileToS3 } from '../../api/commonAPI/awsUploadAPI';
import { S3_FOLDERS } from '../../config/s3Config';

import {
    fetchNatureofGroups, fetchMasterGroups, fetchSubGroups, fetchStatesList,
    fetchAllTdsCodes, fetchAllSPCategories, fetchSPCategoryDetails,
    saveNewVendorSP, clearMasterGroups, clearSubGroups, clearSaveResult, resetVendorCreationForm,
    selectNatureOfGroupsArray, selectNatureOfGroupsLoading,
    selectMasterGroupsArray, selectMasterGroupsLoading,
    selectSubGroupsArray, selectSubGroupsLoading,
    selectStatesListArray, selectStatesListLoading,
    selectTdsCodesArray, selectTdsCodesLoading,
    selectSPCategoriesArray, selectSPCategoriesLoading, selectSPCategoryDetails,
    selectSaveStatus, selectSaveLoading, selectSaveError,
} from '../../slices/purchaseSlice/vendorCreationSlice';

import { updateVendor } from '../../api/businessinfoAPI/vendorAPI';
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const EMAIL_REGEX = /^([\w-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(]?)$/;

// The 15 compliance/document proof fields; which are editable/required depends on the SP Category (see SP_DOC_* below).
const DOC_FIELDS = [
    { key: 'OwnerIDKYC', label: 'Owner ID / KYC', required: true },
    { key: 'VehicleEquipmentRCInsurance', label: 'Vehicle / Equipment RC & Insurance' },
    { key: 'ESIRegistration', label: 'ESI Registration' },
    { key: 'LabourLicense', label: 'Labour License' },
    { key: 'ElectricalContractorLicense', label: 'Electrical Contractor License' },
    { key: 'InsuranceWCLiability', label: 'Insurance (WC / Liability)' },
    { key: 'PastExperienceProof', label: 'Past Experience Proof' },
    { key: 'ManpowerListCertifications', label: 'Manpower List & Certifications' },
    { key: 'ToolsEquipmentList', label: 'Tools & Equipment List' },
    { key: 'QAQCProcedures', label: 'QA/QC Procedures' },
    { key: 'HSEPolicySafetyPlan', label: 'HSE Policy / Safety Plan' },
    { key: 'FinancialStatementsITR', label: 'Financial Statements / ITR' },
    { key: 'BankStatementWCProof', label: 'Bank Statement / WC Proof' },
    { key: 'PerformanceBGSecurity', label: 'Performance BG / Security' },
    { key: 'ClientAlignedB2BDocs', label: 'Client-Aligned B2B Docs' },
];

// Legacy AddNewServiceProviderVendor rules, keyed by the category's position in the SP Category list (1-based).
// Read-only fields can't be filled for that category; required fields are always editable for it.
const SP_DOC_READONLY = {
    1: ['ESIRegistration', 'LabourLicense', 'ElectricalContractorLicense', 'PastExperienceProof', 'ManpowerListCertifications',
        'ToolsEquipmentList', 'QAQCProcedures', 'HSEPolicySafetyPlan', 'FinancialStatementsITR', 'BankStatementWCProof',
        'PerformanceBGSecurity', 'ClientAlignedB2BDocs'],
    2: ['ElectricalContractorLicense', 'ToolsEquipmentList', 'QAQCProcedures', 'FinancialStatementsITR', 'BankStatementWCProof',
        'PerformanceBGSecurity', 'ClientAlignedB2BDocs'],
    3: ['PerformanceBGSecurity', 'ClientAlignedB2BDocs'],
    4: [],
};
const SP_DOC_REQUIRED = {
    1: ['OwnerIDKYC'],
    2: ['OwnerIDKYC', 'ESIRegistration', 'LabourLicense'],
    3: ['OwnerIDKYC', 'InsuranceWCLiability', 'PastExperienceProof', 'ManpowerListCertifications', 'ToolsEquipmentList', 'HSEPolicySafetyPlan'],
    4: DOC_FIELDS.map((f) => f.key),
};
const SP_PF_REQUIRED = [2, 4];

const MONTHS3 = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };
const toDateOrNull = (v) => {
    if (!v) return null;
    const m = String(v).match(/^(\d{1,2})[-\s/]([A-Za-z]{3})[-\s/](\d{4})/);
    if (m && MONTHS3[m[2].toLowerCase()] !== undefined) return new Date(Number(m[3]), MONTHS3[m[2].toLowerCase()], Number(m[1]));
    const d = new Date(String(v).split('T')[0] + 'T00:00:00');
    return isNaN(d.getTime()) ? null : d;
};

// editData (legacy /Purchase/EditVendor): a returned vendor (GetVerificationVendorbyCode) — resubmitted via
// PUT UpdateVendor with Action 'Update', CheckUpdationType 'ReturnUpdate'; success = 'Updated'.
const AddServiceProviderVendor = ({ onSuccess, onCancel, editData = null }) => {
    const isEdit = !!editData;
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    const natureOfGroups = useSelector(selectNatureOfGroupsArray);
    const natureLoading = useSelector(selectNatureOfGroupsLoading);
    const masterGroups = useSelector(selectMasterGroupsArray);
    const masterLoading = useSelector(selectMasterGroupsLoading);
    const subGroups = useSelector(selectSubGroupsArray);
    const subLoading = useSelector(selectSubGroupsLoading);
    const statesList = useSelector(selectStatesListArray);
    const statesLoading = useSelector(selectStatesListLoading);
    const tdsCodes = useSelector(selectTdsCodesArray);
    const tdsLoading = useSelector(selectTdsCodesLoading);
    const spCategories = useSelector(selectSPCategoriesArray);
    const spCategoriesLoading = useSelector(selectSPCategoriesLoading);
    const spCategoryDetails = useSelector(selectSPCategoryDetails);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    const [spCategory, setSpCategory] = useState(editData?.SPCategory || '');
    const [vendorName, setVendorName] = useState(editData?.VendorName || '');
    const prefillRef = useRef(isEdit ? {
        groupId: editData.GroupId ? String(editData.GroupId) : '',
        subGroupId: editData.SubGroupId ? String(editData.SubGroupId) : '',
    } : null);
    const [phoneNo, setPhoneNo] = useState(editData?.Phoneno || '');
    const [mobileNo, setMobileNo] = useState(editData?.MobileNo || '');
    const [bankName, setBankName] = useState(editData?.BankName || '');
    const [accountNo, setAccountNo] = useState(editData?.AccountNo || '');
    const [ifscCode, setIfscCode] = useState(editData?.IFSCode || '');
    const [address, setAddress] = useState(editData?.Address || '');

    const [natureGroupId, setNatureGroupId] = useState(editData?.NatureGroupId ? String(editData.NatureGroupId) : '');
    const [groupId, setGroupId] = useState('');
    const [subGroupId, setSubGroupId] = useState('');
    const [openingBalance, setOpeningBalance] = useState(editData?.OpeningBalance != null ? String(editData.OpeningBalance) : '');
    const [balanceDate, setBalanceDate] = useState(() => toDateOrNull(editData?.BalanceAsOnDate));
    const [ledgerValueType, setLedgerValueType] = useState(editData?.LedgerValueType || '');
    const [gstApplicable, setGstApplicable] = useState(() => (editData && /^(yes|1)$/i.test(String(editData.GstApplicable || '')) ? 'Yes' : 'No'));
    const [gstRows, setGstRows] = useState(() => (editData?.VendorGSTData?.length
        ? editData.VendorGSTData.map((g) => ({ ...newGSTRow(), stateId: String(g.StateId || ''), gstNo: g.TaxNo || '', checked: true }))
        : [newGSTRow()]));
    const [msmeApplicable, setMsmeApplicable] = useState(() => (editData && /^yes$/i.test(String(editData.MSMEApplicable || '')) ? 'Yes' : 'No'));
    const [udyamNo, setUdyamNo] = useState(editData?.UdyamRegistrationNumber || '');
    const [enterpriseType, setEnterpriseType] = useState(editData?.Enterprisetype || '');
    const [majorActivity, setMajorActivity] = useState(editData?.MajorActivity || '');
    const [classificationDate, setClassificationDate] = useState(() => toDateOrNull(editData?.ClassificationEffectiveDate));
    const [commencementDate, setCommencementDate] = useState(() => toDateOrNull(editData?.DateofcommencementofBusinessorProduction));

    const [email, setEmail] = useState(editData?.EmailId || '');
    const [panNo, setPanNo] = useState(editData?.PanNo || '');
    const [pfRegNo, setPfRegNo] = useState(editData?.PFRegNo || '');
    const [tdsApplicable, setTdsApplicable] = useState(editData?.TDSApplicable || '');
    const [tdsCode, setTdsCode] = useState(editData?.TDSCode || '');

    const [docFields, setDocFields] = useState(() => Object.fromEntries(DOC_FIELDS.map((f) => [f.key, editData?.[f.key] || ''])));
    const skipDocResetRef = useRef(isEdit);
    const categoryIndex = spCategories.findIndex((c) => String(c.CType) === String(spCategory)) + 1;
    const isDocReadOnly = (key) => !categoryIndex || (SP_DOC_READONLY[categoryIndex] || []).includes(key);
    const isDocRequired = (key) => (SP_DOC_REQUIRED[categoryIndex] || ['OwnerIDKYC']).includes(key);
    const [approvalValidity, setApprovalValidity] = useState(editData?.ApprovalValidity || '');
    const [userRemarks, setUserRemarks] = useState('');

    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        dispatch(fetchNatureofGroups());
        dispatch(fetchStatesList());
        dispatch(fetchAllTdsCodes());
        dispatch(fetchAllSPCategories());
        return () => { dispatch(resetVendorCreationForm()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        setGroupId(prefillRef.current?.groupId || ''); setSubGroupId('');
        if (prefillRef.current) prefillRef.current = { ...prefillRef.current, groupId: '' };
        dispatch(clearMasterGroups()); dispatch(clearSubGroups());
        if (natureGroupId) dispatch(fetchMasterGroups(natureGroupId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [natureGroupId]);

    useEffect(() => {
        setSubGroupId(prefillRef.current?.subGroupId || '');
        if (prefillRef.current?.subGroupId && groupId) prefillRef.current = null;
        dispatch(clearSubGroups());
        if (groupId) dispatch(fetchSubGroups(groupId));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId]);

    useEffect(() => {
        if (gstApplicable === 'No') setGstRows([newGSTRow()]);
    }, [gstApplicable]);

    useEffect(() => {
        if (spCategory) dispatch(fetchSPCategoryDetails(spCategory));
        // keep the returned vendor's stored documents on the first (prefill) run
        if (skipDocResetRef.current) { skipDocResetRef.current = false; return; }
        setDocFields(Object.fromEntries(DOC_FIELDS.map((f) => [f.key, ''])));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spCategory]);

    const isBusy = saveLoading || uploading;

    const handleReset = () => {
        setSpCategory(''); setVendorName(''); setPhoneNo(''); setMobileNo('');
        setBankName(''); setAccountNo(''); setIfscCode(''); setAddress('');
        setNatureGroupId(''); setGroupId(''); setSubGroupId('');
        setOpeningBalance(''); setBalanceDate(null); setLedgerValueType('');
        setGstApplicable('No'); setGstRows([newGSTRow()]);
        setMsmeApplicable('No'); setUdyamNo(''); setEnterpriseType(''); setMajorActivity('');
        setClassificationDate(null); setCommencementDate(null);
        setEmail(''); setPanNo(''); setPfRegNo(''); setTdsApplicable(''); setTdsCode('');
        setDocFields(Object.fromEntries(DOC_FIELDS.map((f) => [f.key, ''])));
        setApprovalValidity(''); setUserRemarks(''); setFile(null);
        dispatch(resetVendorCreationForm());
        dispatch(fetchNatureofGroups());
        dispatch(fetchStatesList());
        dispatch(fetchAllTdsCodes());
        dispatch(fetchAllSPCategories());
    };

    const handleGSTRowChange = (id, field, value) => {
        setGstRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
    };

    const handleSubmit = async () => {
        if (!spCategory) { toast.warn('Select Service Provider Category.'); return; }
        if (!vendorName.trim()) { toast.warn('Enter Vendor Name.'); return; }
        if (!/^\d{10}$/.test(phoneNo)) { toast.warn('Enter a valid 10-digit Phone No.'); return; }
        if (!/^\d{10}$/.test(mobileNo)) { toast.warn('Enter a valid 10-digit Mobile No.'); return; }
        if (!bankName.trim()) { toast.warn('Enter Bank Name.'); return; }
        if (!accountNo.trim()) { toast.warn('Enter Account No.'); return; }
        if (!ifscCode.trim()) { toast.warn('Enter IFSC Code.'); return; }
        if (!address.trim()) { toast.warn('Enter Address.'); return; }
        if (!ledgerValueType) { toast.warn('Select Ledger Value Type.'); return; }
        if (!natureGroupId) { toast.warn('Select Nature of Group.'); return; }
        if (!groupId) { toast.warn('Select Master Group.'); return; }
        if (subGroups.length > 0 && !subGroupId) { toast.warn('Select Sub Group.'); return; }
        if (!openingBalance) { toast.warn('Enter Opening Balance.'); return; }
        if (!balanceDate) { toast.warn('Select Balance As On Date.'); return; }
        if (msmeApplicable === 'Yes') {
            if (!udyamNo.trim()) { toast.warn('Enter Udyam Registration Number.'); return; }
            if (!enterpriseType) { toast.warn('Select Enterprise Type.'); return; }
            if (!majorActivity) { toast.warn('Select Major Activity.'); return; }
            if (!classificationDate) { toast.warn('Select Classification Effective Date.'); return; }
            if (!commencementDate) { toast.warn('Select Date of Commencement of Business/Production.'); return; }
        }
        if (!email.trim() || !EMAIL_REGEX.test(email)) { toast.warn('Enter a valid Email.'); return; }
        if (!panNo.trim()) { toast.warn('Enter PAN No.'); return; }
        if (!tdsApplicable) { toast.warn('Select TDS Applicable.'); return; }
        if (tdsApplicable === 'Yes' && !tdsCode) { toast.warn('Select TDS Code.'); return; }
        if (gstApplicable === 'Yes') {
            const gstErr = validateGSTRows(gstRows);
            if (gstErr) { toast.warn(gstErr); return; }
        }
        if (SP_PF_REQUIRED.includes(categoryIndex) && !pfRegNo.trim()) { toast.warn('Enter PF Reg Number.'); return; }
        for (const f of DOC_FIELDS) {
            if (isDocRequired(f.key) && !docFields[f.key]?.trim()) { toast.warn(`Enter ${f.label}.`); return; }
        }
        if (!approvalValidity.trim()) { toast.warn('Enter Approval Validity.'); return; }
        if (!userRemarks.trim()) { toast.warn('Enter User Remarks.'); return; }
        if (!isEdit && !file) { toast.warn('Upload the vendor document (PDF).'); return; }

        const gstStatesList = gstApplicable === 'Yes' ? gstRows.map((r) => r.stateId).join(',') + ',' : '';
        const gstNoList = gstApplicable === 'Yes' ? gstRows.map((r) => r.gstNo).join(',') + ',' : '';
        const extension = file ? file.name.split('.').pop() : '';

        const payload = {
            SPCategory: spCategory,
            PanNo: panNo,
            PFRegNo: pfRegNo,
            VendorName: vendorName,
            Phoneno: phoneNo,
            MobileNo: mobileNo,
            BankName: bankName,
            AccountNo: accountNo,
            IFSCode: ifscCode,
            Address: address,
            NatureGroupId: natureGroupId,
            GroupId: groupId,
            SubGroupId: subGroupId ? parseInt(subGroupId, 10) : 0,
            Action: 'Add',
            GstApplicable: gstApplicable === 'Yes' ? '1' : '0',
            GSTStatesList: gstStatesList,
            GSTNoList: gstNoList,
            TDSApplicable: tdsApplicable,
            TDSCode: tdsApplicable === 'Yes' ? tdsCode : '',
            LedgerValueType: ledgerValueType,
            OpeningBalance: parseFloat(openingBalance) || 0,
            BalanceAsOnDate: formatDateForAPI(balanceDate),
            MSMEApplicable: msmeApplicable,
            EmailId: email,
            Filechk: file ? 'Yes' : 'No',
            Extension: extension,
            UdyamRegistrationNumber: msmeApplicable === 'Yes' ? udyamNo : '',
            Enterprisetype: msmeApplicable === 'Yes' ? enterpriseType : '',
            MajorActivity: msmeApplicable === 'Yes' ? majorActivity : '',
            ClassificationEffectiveDate: msmeApplicable === 'Yes' ? formatDateForAPI(classificationDate) : '',
            DateofcommencementofBusinessorProduction: msmeApplicable === 'Yes' ? formatDateForAPI(commencementDate) : '',
            ...docFields,
            ApprovalValidity: approvalValidity,
            UserRemarks: userRemarks,
            RoleId: roleId,
            CreatedBy: userName,
        };

        if (isEdit) {
            setUploading(true);
            try {
                const res = await updateVendor({
                    ...payload, VendorCode: editData.VendorCode, Action: 'Update', CheckUpdationType: 'ReturnUpdate',
                });
                const responseStr = String(res?.Data ?? res ?? '').trim();
                const [status, newKey] = responseStr.split(',').map((x) => (x || '').trim());
                if (status !== 'Updated') { toast.error(responseStr || 'Error Occurred While Updating Vendor'); return; }
                const key = newKey || editData.uploaddoc;
                if (file && key) {
                    try { await uploadFileToS3(file, S3_FOLDERS.VENDOR_DETAILS, key); }
                    catch { toast.warn('Vendor updated, but the document upload failed.'); }
                }
                toast.success('Vendor Updated Successfully');
                onSuccess?.();
            } catch (e) {
                toast.error(e?.Message || e?.message || 'Error Occurred While Updating Vendor');
            } finally { setUploading(false); }
            return;
        }

        const result = await dispatch(saveNewVendorSP(payload));
        if (saveNewVendorSP.fulfilled.match(result)) {
            const responseStr = typeof result.payload?.Data === 'string' ? result.payload.Data : '';
            const [status, downloadExt] = responseStr.split(',');
            if (status === 'Submited') {
                if (file && downloadExt) {
                    setUploading(true);
                    try {
                        await uploadFileToS3(file, S3_FOLDERS.VENDOR_DETAILS, downloadExt);
                    } catch {
                        toast.warn('Vendor saved, but the document upload failed. Please retry the upload separately.');
                    } finally {
                        setUploading(false);
                    }
                }
                toast.success('Vendor Details Added Successfully.');
                dispatch(clearSaveResult());
                handleReset();
                onSuccess?.();
            }
        }
    };

    useEffect(() => {
        if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                <SectionHeader icon={Briefcase} title="Add Service Provider Vendor" subtitle="SP category, identity and compliance details"
                    action={
                        <button onClick={onCancel} disabled={isBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                            Close
                        </button>
                    }
                />
                <div className="p-6 md:p-8">
                    <InnerHeader icon={Briefcase} title="Service Provider Category" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <Label required>SP Category</Label>
                            <div className="relative">
                                <select className={`${inputCls} appearance-none pr-10`} value={spCategory} onChange={(e) => setSpCategory(e.target.value)} disabled={isBusy || spCategoriesLoading}>
                                    <option value="">{spCategoriesLoading ? 'Loading…' : '-Please Select-'}</option>
                                    {spCategories.map((c) => <option key={c.CType} value={c.CType}>{c.CName}</option>)}
                                </select>
                                <SelectIcon loading={spCategoriesLoading} />
                            </div>
                        </div>
                        <div>
                            <Label>Examples</Label>
                            <input type="text" readOnly value={spCategoryDetails?.CDetails || ''} className={inputCls} />
                        </div>
                    </div>

                    <InnerHeader icon={Building2} title="Vendor Details" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <Label required>Vendor Name</Label>
                            <input type="text" className={inputCls} value={vendorName} onChange={(e) => setVendorName(e.target.value)} disabled={isBusy} />
                        </div>
                        <div>
                            <Label required>Phone No</Label>
                            <input type="text" maxLength={10} className={inputCls} value={phoneNo} onChange={(e) => /^\d*$/.test(e.target.value) && setPhoneNo(e.target.value)} disabled={isBusy} />
                        </div>
                        <div>
                            <Label required>Mobile No</Label>
                            <input type="text" maxLength={10} className={inputCls} value={mobileNo} onChange={(e) => /^\d*$/.test(e.target.value) && setMobileNo(e.target.value)} disabled={isBusy} />
                        </div>
                        <div>
                            <Label required>Email</Label>
                            <input type="text" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} disabled={isBusy} />
                        </div>
                        <div>
                            <Label required>PAN No</Label>
                            <input type="text" className={inputCls} value={panNo} onChange={(e) => setPanNo(e.target.value)} disabled={isBusy} />
                        </div>
                        <div>
                            <Label>PF Reg No</Label>
                            <input type="text" className={inputCls} value={pfRegNo} onChange={(e) => setPfRegNo(e.target.value)} disabled={isBusy} />
                        </div>
                    </div>

                    <InnerHeader icon={Landmark} title="Bank Details" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <Label required>Bank Name</Label>
                            <input type="text" className={inputCls} value={bankName} onChange={(e) => setBankName(e.target.value)} disabled={isBusy} />
                        </div>
                        <div>
                            <Label required>Account No</Label>
                            <input type="text" className={inputCls} value={accountNo} onChange={(e) => setAccountNo(e.target.value)} disabled={isBusy} />
                        </div>
                        <div>
                            <Label required>IFSC Code</Label>
                            <input type="text" className={inputCls} value={ifscCode} onChange={(e) => setIfscCode(e.target.value)} disabled={isBusy} />
                        </div>
                        <div className="md:col-span-3">
                            <Label required>Address</Label>
                            <textarea rows={2} className={`${inputCls} resize-none`} value={address} onChange={(e) => setAddress(e.target.value)} disabled={isBusy} />
                        </div>
                    </div>

                    <InnerHeader icon={CreditCard} title="Group & Ledger" />
                    <div className="mb-6">
                        <GroupCascadeFields
                            natureOfGroups={natureOfGroups} natureLoading={natureLoading} natureGroupId={natureGroupId} onNatureChange={setNatureGroupId}
                            masterGroups={masterGroups} masterLoading={masterLoading} groupId={groupId} onGroupChange={setGroupId}
                            subGroups={subGroups} subLoading={subLoading} subGroupId={subGroupId} onSubGroupChange={setSubGroupId}
                            disabled={isBusy}
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div>
                            <Label required>Opening Balance (₹)</Label>
                            <input type="text" inputMode="decimal" className={inputCls} value={openingBalance}
                                onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setOpeningBalance(v); }} disabled={isBusy} />
                        </div>
                        <div>
                            <Label required>Balance As On Date</Label>
                            <CustomDatePicker value={balanceDate} onChange={setBalanceDate} disabled={isBusy} maxDate={new Date()} />
                        </div>
                        <div>
                            <Label required>Ledger Value Type</Label>
                            <ToggleYesNo value={ledgerValueType} onChange={setLedgerValueType} yesLabel="Credit" noLabel="Debit" disabled={isBusy} />
                        </div>
                    </div>

                    <InnerHeader icon={ShieldCheck} title="TDS, GST & MSME" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <Label required>TDS Applicable</Label>
                            <div className="max-w-xs">
                                <ToggleYesNo value={tdsApplicable} onChange={setTdsApplicable} disabled={isBusy} />
                            </div>
                        </div>
                        {tdsApplicable === 'Yes' && (
                            <div>
                                <Label required>TDS Code</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={tdsCode} onChange={(e) => setTdsCode(e.target.value)} disabled={isBusy || tdsLoading}>
                                        <option value="">{tdsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {tdsCodes.map((t) => <option key={t.TdsCode} value={t.TdsCode}>{t.TdsName}</option>)}
                                    </select>
                                    <SelectIcon loading={tdsLoading} />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mb-6">
                        <Label required>GST Applicable</Label>
                        <div className="max-w-xs mb-4">
                            <ToggleYesNo value={gstApplicable} onChange={setGstApplicable} disabled={isBusy} />
                        </div>
                        {gstApplicable === 'Yes' && (
                            <GSTDetailsTable
                                rows={gstRows} statesList={statesList}
                                onAddRow={() => setGstRows((p) => [...p, newGSTRow()])}
                                onRemoveRow={(id) => setGstRows((p) => p.filter((r) => r.id !== id))}
                                onRowChange={handleGSTRowChange}
                                disabled={isBusy || statesLoading}
                            />
                        )}
                    </div>
                    <div className="mb-6">
                        <MSMEFields
                            applicable={msmeApplicable} onApplicableChange={setMsmeApplicable}
                            udyamNo={udyamNo} onUdyamNoChange={setUdyamNo}
                            enterpriseType={enterpriseType} onEnterpriseTypeChange={setEnterpriseType}
                            majorActivity={majorActivity} onMajorActivityChange={setMajorActivity}
                            classificationDate={classificationDate} onClassificationDateChange={setClassificationDate}
                            commencementDate={commencementDate} onCommencementDateChange={setCommencementDate}
                            disabled={isBusy}
                        />
                    </div>

                    <InnerHeader icon={ClipboardList} title="Compliance & Documentation" subtitle="Required fields depend on the Service Provider Category" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        {DOC_FIELDS.map((f) => (
                            <div key={f.key}>
                                <Label required={isDocRequired(f.key)}>{f.label}</Label>
                                <input
                                    type="text"
                                    className={inputCls}
                                    value={docFields[f.key]}
                                    onChange={(e) => setDocFields((p) => ({ ...p, [f.key]: e.target.value }))}
                                    readOnly={isDocReadOnly(f.key)}
                                    disabled={isBusy}
                                />
                            </div>
                        ))}
                        <div>
                            <Label required>Approval Validity</Label>
                            <input type="text" className={inputCls} value={approvalValidity} onChange={(e) => setApprovalValidity(e.target.value)} disabled={isBusy} />
                        </div>
                    </div>

                    <Label required>User Remarks</Label>
                    <textarea rows={3} className={`${inputCls} resize-none mb-6`} value={userRemarks} onChange={(e) => setUserRemarks(e.target.value)} disabled={isBusy} />

                    <InnerHeader icon={FileStack} title="Document" />
                    <DocumentUploadField file={file} onFileChange={setFile} disabled={isBusy} />
                </div>
            </div>

            <div className="flex items-center justify-end gap-3">
                <button onClick={handleReset} disabled={isBusy}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                    <RotateCcw className="h-4 w-4" /> Reset
                </button>
                <button onClick={handleSubmit} disabled={isBusy}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                    {isBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> {uploading ? 'Uploading…' : 'Saving…'}</> : <><Send className="h-4 w-4" /> Submit</>}
                </button>
            </div>
        </div>
    );
};

export default AddServiceProviderVendor;
